import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../lib/api.js';
import { avatarUrl, logout, removeAvatar, session, setAvatar } from '../lib/engine.js';
import { disablePush, enablePush, installHint, pushState, type PushState } from '../lib/push.js';
import { back } from './nav.js';
import { Avatar } from './Avatar.js';

export function Settings() {
  const me = session.value!;
  const [push, setPush] = useState<PushState>(() => pushState());
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invites, setInvites] = useState<{ code: string; used: boolean; expiresAt: number | null }[]>(
    [],
  );
  const [health, setHealth] = useState<{
    subscriptions: number;
    failing: number;
    neverDelivered: number;
    proxyEnabled: boolean;
  } | null>(null);

  useEffect(() => {
    void api
      .listInvites()
      .then((result) => setInvites(result.invites.filter((invite) => !invite.used)))
      .catch(() => undefined);
    void api.pushHealth().then(setHealth).catch(() => undefined);
  }, []);

  const guard = async (label: string, action: () => Promise<void>) => {
    setBusy(label);
    setError(null);
    setNote(null);
    try {
      await action();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? (caught.details as { hint?: string })?.hint ?? `Ошибка: ${caught.code}`
          : caught instanceof Error
            ? caught.message
            : 'Не получилось',
      );
    } finally {
      setBusy(null);
    }
  };

  const hint = installHint();

  return (
    <div class="screen">
      <header class="topbar">
        <button class="icon-btn" onClick={back} aria-label="Назад">
          ‹
        </button>
        <h1>Настройки</h1>
      </header>

      <div class="scroll">
        <div class="section">
          <h2>Профиль</h2>
          <div class="row">
            <div class="row" style="gap:12px">
              <Avatar userId={me.userId} name={me.displayName} size={56} />
              <div>
                <div>{me.displayName}</div>
                <div class="preview">@{me.username}</div>
              </div>
            </div>
          </div>

          <div class="row">
            <label class="btn subtle" style="flex:1">
              {busy === 'avatar' ? 'Загружаю…' : 'Сменить аватарку'}
              <input
                type="file"
                accept="image/*"
                hidden
                disabled={busy !== null}
                onChange={(event) => {
                  const input = event.target as HTMLInputElement;
                  const file = input.files?.[0];
                  // Сбрасываем сразу: иначе повторный выбор того же файла
                  // не вызовет событие, и кнопка будет выглядеть сломанной.
                  input.value = '';
                  if (!file) return;

                  void guard('avatar', async () => {
                    await setAvatar(file);
                    setNote('Аватарка обновлена. Её увидят все, с кем у тебя есть общий чат.');
                  });
                }}
              />
            </label>
            {avatarUrl(me.userId) && (
              <button
                class="btn danger"
                disabled={busy !== null}
                onClick={() => guard('avatar-remove', async () => void (await removeAvatar()))}
              >
                Убрать
              </button>
            )}
          </div>

          <div class="notice">
            Аватарка шифруется отдельным ключом, а ключ запечатывается для каждого, с кем
            у тебя есть общий чат. Сервер хранит картинку, но открыть её не может — как
            и переписку.
          </div>
        </div>

        {(error || note) && (
          <div class="section">
            {error && <div class="error">{error}</div>}
            {note && <div class="notice">{note}</div>}
          </div>
        )}

        <div class="section">
          <h2>Уведомления</h2>

          <div class="row">
            <span>
              <span class={`status-dot ${push === 'granted' ? 'ok' : 'warn'}`} />
              {describePush(push)}
            </span>
            {push === 'prompt' && (
              <button
                class="btn"
                disabled={busy !== null}
                onClick={() =>
                  guard('enable', async () => {
                    const result = await enablePush();
                    setPush(result.state);
                    // Отказ без объяснения выглядит как несработавшая кнопка:
                    // состояние то же, надпись та же, реакции никакой.
                    if (!result.ok && result.reason) throw new Error(result.reason);
                  })
                }
              >
                Включить
              </button>
            )}
            {push === 'granted' && (
              <button
                class="btn subtle"
                disabled={busy !== null}
                onClick={() =>
                  guard('disable', async () => {
                    await disablePush();
                    setPush(pushState());
                  })
                }
              >
                Отключить
              </button>
            )}
          </div>

          {hint && <div class="notice">{hint}</div>}

          {/*
            Кнопка проверки — самый полезный инструмент во всём приложении.
            Доставка пушей идёт через FCM, Mozilla или APNs, то есть через
            единственное звено, которое мы не контролируем. Если уведомления
            «не приходят», проверять надо отсюда и на каждом устройстве отдельно.
          */}
          {push === 'granted' && (
            <>
              <button
                class="btn subtle"
                disabled={busy !== null}
                onClick={() =>
                  guard('test', async () => {
                    const result = await api.pushTest();
                    setNote(
                      `Отправлено на ${result.sentTo} ${plural(result.sentTo, 'устройство', 'устройства', 'устройств')}. ` +
                        'Уведомление должно прийти за несколько секунд. Если не пришло — ' +
                        'значит push-сервис браузера недоступен с этого устройства или с сервера.',
                    );
                  })
                }
              >
                {busy === 'test' ? 'Отправляю…' : 'Отправить проверочное уведомление'}
              </button>

              {health && (
                <div class="notice">
                  Подписок на сервере: <strong>{health.subscriptions}</strong>
                  {health.failing > 0 && <> · со сбоями: <strong>{health.failing}</strong></>}
                  {health.neverDelivered > 0 && (
                    <> · ни разу не доставлено: <strong>{health.neverDelivered}</strong></>
                  )}
                  {health.proxyEnabled && <> · отправка через прокси</>}
                </div>
              )}
            </>
          )}
        </div>

        <div class="section">
          <h2>Приглашения</h2>
          <button
            class="btn subtle"
            disabled={busy !== null}
            onClick={() =>
              guard('invite', async () => {
                const { code } = await api.createInvite(14);
                setInvites((current) => [{ code, used: false, expiresAt: null }, ...current]);
              })
            }
          >
            {busy === 'invite' ? 'Создаю…' : 'Выписать код на 14 дней'}
          </button>

          {invites.map((invite) => (
            <div class="mono" key={invite.code}>
              {invite.code}
            </div>
          ))}

          <div class="notice">
            Код одноразовый. Регистрация без кода закрыта, так что случайных людей
            на сервере не появится.
          </div>
        </div>

        <div class="section">
          <h2>Приложение</h2>
          <div class="row">
            <span class="preview">Сборка {__BUILD_ID__}</span>
            <button
              class="btn subtle"
              disabled={busy !== null}
              onClick={() =>
                guard('refresh', async () => {
                  // Кеши сносим, но регистрацию Service Worker не трогаем:
                  // снятие регистрации убило бы подписку на уведомления,
                  // и их пришлось бы включать заново.
                  await Promise.all((await caches.keys()).map((key) => caches.delete(key)));
                  const registration = await navigator.serviceWorker?.getRegistration();
                  await registration?.update();
                  location.reload();
                })
              }
            >
              Обновить
            </button>
          </div>
          <div class="notice">
            Если после обновления сервера приложение ведёт себя по-старому — нажми
            «Обновить». Сверь отметку сборки с той, что сейчас на сервере: совпадают —
            значит клиент свежий и дело не в нём.
          </div>
        </div>

        <div class="section">
          <h2>Выход</h2>
          <button
            class="btn danger"
            disabled={busy !== null}
            onClick={() =>
              guard('logout', async () => {
                await logout();
              })
            }
          >
            Выйти и стереть данные на этом устройстве
          </button>
          <div class="notice">
            Выход удаляет с устройства ключи и всю историю. Переписка останется на сервере
            в зашифрованном виде и вернётся при следующем входе — но только если помнишь
            пароль: без него ключи не расшифровать.
          </div>
        </div>
      </div>
    </div>
  );
}

function describePush(state: PushState): string {
  switch (state) {
    case 'granted':
      return 'Включены';
    case 'denied':
      return 'Запрещены в настройках браузера';
    case 'needs-install':
      return 'Нужно добавить приложение на домашний экран';
    case 'unsupported':
      return 'Этот браузер не поддерживает push-уведомления';
    default:
      return 'Выключены';
  }
}

function plural(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}
